import { useAutoAnimate } from "@formkit/auto-animate/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Textarea } from "@mantine/core";
import { memoryRequest } from "api/assistantMemory";
import MemoryAlert from "./MemoryAlert";
import MemoryButton from "./MemoryButton";
import { textareaStyles } from "./memoryFieldStyles";
import css from "./memory.module.scss";

const labels = {
  pending: "Saved. Waiting to process.",
  processing: "Saved. Processing for assistant use.",
  ready: "Ready for assistant use.",
  needs_clarification: "Saved. Needs clarification.",
  partial: "Some memories are ready. Other details need clarification.",
  failed: "Text saved. Processing failed.",
};
export default function MemorySubmissions({
  refreshKey,
  recallEnabled = true,
  onCompleted,
}) {
  const [parent] = useAutoAnimate({ duration: 200 });
  const completed = useRef(new Set());
  const observedPending = useRef(new Set());
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null);
  const [clarifying, setClarifying] = useState(null);
  const [text, setText] = useState("");
  const [morePending, setMorePending] = useState(false);
  const generation = useRef(0);
  const controller = useRef(null);
  const load = useCallback(async () => {
    const version = ++generation.current;
    const signal = controller.current?.signal;
    const result = await memoryRequest("/memory-submissions", { signal });
    if (version !== generation.current || signal?.aborted) return;
    const submissions = result.submissions || [];
    for (const item of submissions) {
      if (!["ready", "cancelled"].includes(item.status))
        observedPending.current.add(item.id);
    }
    // Historical completions must never render, even while the list refreshes.
    setItems(
      submissions.filter(
        (item) =>
          item.status !== "ready" || observedPending.current.has(item.id),
      ),
    );
    setMorePending(Boolean(result.morePending));
    setError("");
    const ready = submissions.filter(
      (item) => item.status === "ready" && !completed.current.has(item.id),
    );
    if (ready.length && onCompleted) {
      // Keep confirmation visible until the canonical list has refreshed successfully.
      await onCompleted();
      if (version !== generation.current || signal?.aborted) return;
      ready.forEach((item) => completed.current.add(item.id));
      setItems(submissions.filter((item) => !completed.current.has(item.id)));
    }
  }, [onCompleted]);
  useEffect(() => {
    const activeController = new AbortController();
    controller.current = activeController;
    let running = false;
    const refresh = async () => {
      if (running || document.visibilityState === "hidden") return;
      running = true;
      try {
        await load();
      } catch (e) {
        if (!activeController.signal.aborted) setError(e.message);
      } finally {
        running = false;
      }
    };
    void refresh();
    const timer = setInterval(refresh, 5000);
    return () => {
      activeController.abort();
      clearInterval(timer);
    };
  }, [load, refreshKey]);
  const act = async (item, action) => {
    if (busy) return;
    setBusy(item.id);
    setError("");
    try {
      await memoryRequest(`/memory-submissions/${item.id}/${action}`, {
        method: "POST",
        body: action === "clarify" ? { text } : {},
        signal: controller.current.signal,
      });
      setClarifying(null);
      setText("");
      await load();
    } catch (e) {
      if (!controller.current.signal.aborted) setError(e.message);
    } finally {
      setBusy(null);
    }
  };
  const visible = items.filter(
    (item) => item.status !== "cancelled" && !completed.current.has(item.id),
  );
  return (
    <div ref={parent} className={css.group} aria-label="Memory submissions">
      {error && (
        <MemoryAlert tone="danger">
          <span>{error}</span>
          <MemoryButton
            onClick={() => load().catch((e) => setError(e.message))}
          >
            Refresh submissions
          </MemoryButton>
        </MemoryAlert>
      )}
      {morePending && (
        <MemoryAlert>
          Showing the first 200 pending submissions. Resolve these to see the
          remaining submissions.
        </MemoryAlert>
      )}
      {visible.map((item) => (
        <article key={item.id} className={css.shell}>
          <div className={`${css.core} ${css.cardCore}`}>
            <p role="status">
              {item.status === "ready" && !recallEnabled
                ? "Validated. Recall is turned off."
                : labels[item.status] || "Text saved."}
            </p>
            <p className={css.cardText}>{item.text}</p>
            {item.reason && (
              <p>
                {item.status === "failed"
                  ? "You can retry processing. Your original text is still saved."
                  : item.reason}
              </p>
            )}
            {(item.results || [])
              .filter((result) => result.status === "needs_clarification")
              .map((result) => (
                <p key={result.key}>{result.reason}</p>
              ))}
            {clarifying === item.id && (
              <Textarea
                label="Clarify these details"
                styles={textareaStyles}
                value={text}
                onChange={(event) => setText(event.currentTarget.value)}
                maxLength={4000}
                autosize
                disabled={Boolean(busy)}
              />
            )}
            {item.status !== "ready" && (
              <div className={css.cardActions}>
                {item.status === "failed" && (
                  <MemoryButton
                    loading={busy === item.id}
                    disabled={Boolean(busy)}
                    onClick={() => act(item, "retry")}
                  >
                    Retry processing
                  </MemoryButton>
                )}
                {["needs_clarification", "partial", "failed"].includes(
                  item.status,
                ) &&
                  (clarifying === item.id ? (
                    <MemoryButton
                      loading={busy === item.id}
                      disabled={Boolean(busy) || !text.trim()}
                      onClick={() => act(item, "clarify")}
                    >
                      Submit clarification
                    </MemoryButton>
                  ) : (
                    <MemoryButton
                      disabled={Boolean(busy)}
                      onClick={() => {
                        setClarifying(item.id);
                        setText("");
                      }}
                    >
                      Clarify
                    </MemoryButton>
                  ))}
                <MemoryButton
                  loading={busy === item.id}
                  disabled={Boolean(busy)}
                  onClick={() => act(item, "cancel")}
                >
                  Cancel remaining changes
                </MemoryButton>
              </div>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}
